import React from 'react';
import { XIcon } from 'lucide-react';
import { ToasterState, ToastType, ToastVariantType } from 'src/AppState/ToasterState';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/AppState/Common';
import { cn } from 'src/domains/common/components/utils';

const ToasterWrapper = observer('ToasterWrapper', ({ children }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className='fixed inset-0 z-[100] p-4 flex flex-col-reverse justify-start items-end gap-4 pointer-events-none'>
        {children}
    </div>
));

type ToastContentPropsType = {
    children: React.ReactNode;
    onClick: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    variant: ToastVariantType;
    isClosing: boolean;
};
const ToastContent = observer(
    'ToastContent',
    ({ children, onClick, onMouseEnter, onMouseLeave, isClosing, variant }: ToastContentPropsType) => {
        const variantStyle = variant === 'error' ? 'border-destructive' : 'border-status-success';
        const animation = isClosing === false ? 'animate-toast-open' : 'animate-toast-close';

        return (
            <div
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={cn(
                    'cursor-pointer p-4 border border-solid border-current rounded-md bg-white w-full max-w-md pointer-events-auto flex justify-between items-center',
                    animation,
                    variantStyle
                )}
            >
                {children}
            </div>
        );
    }
);

const ToastTitle = observer('ToastTitle', ({ children }: React.HTMLAttributes<HTMLDivElement>) => (
    <div>{children}</div>
));

const ToastMessage = observer('ToastMessage', ({ children }: React.HTMLAttributes<HTMLDivElement>) => (
    <div>{children}</div>
));

const ToastButton = observer('ToastButton', ({ children }: React.HTMLAttributes<HTMLDivElement>) => (
    <div>{children}</div>
));

type ToastTextWrapperPropsType = {
    children: React.ReactNode;
    variant: ToastVariantType;
};
const ToastTextWrapper = observer('ToastTextWrapper', ({ children, variant }: ToastTextWrapperPropsType) => {
    const variantStyle = variant === 'error' ? 'text-destructive' : 'text-status-success';

    return <div className={cn('text-sm space-y-1', variantStyle)}>{children}</div>;
});

type ToastPropsType = {
    toast: ToastType;
    onClick: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    isClosing: boolean;
};
const Toast = observer(
    'Toast',
    ({
        toast: { title, message, variant, button, persistent },
        onClick,
        onMouseLeave,
        onMouseEnter,
        isClosing,
    }: ToastPropsType) => (
        <ToastContent
            variant={variant}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            isClosing={isClosing}
        >
            <ToastTextWrapper variant={variant}>
                <ToastTitle>{title}</ToastTitle>
                {message === undefined ? null : <ToastMessage>{message}</ToastMessage>}
            </ToastTextWrapper>
            {persistent === true ? (
                <XIcon
                    className='size-4 ml-2 shrink-0 self-start'
                    data-test='toast-close-button'
                />
            ) : (
                <ToastButton>{button}</ToastButton>
            )}
        </ToastContent>
    )
);

export const Toaster = observer('Toaster', () => {
    const common = useCommon();
    const toasterState = ToasterState.get(common);
    if (toasterState.toast === null) {
        return null;
    }
    return (
        <ToasterWrapper>
            <Toast
                toast={toasterState.toast}
                onClick={() => toasterState.closeToast()}
                onMouseEnter={() => toasterState.mouseEnter()}
                onMouseLeave={() => toasterState.mouseLeave()}
                isClosing={toasterState.isClosing}
            />
        </ToasterWrapper>
    );
});
